import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Typography,
  Container,
  TextField,
  Button,
  MenuItem,
  FormControl,
  Grid,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer
} from "@mui/material";
import { format } from "date-fns";

import ErrorPage from "./error-page";

const ChatHeader = () => {
  useEffect(() => {
    document.title = "Booking";
  }, []);

  return (
    <Typography variant="h4" align="center" gutterBottom>
      Book Now
    </Typography>
  );
};

function App() {
  const [selectedDate, setSelectedDate] = useState("");
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [userExists, setUserExists] = useState(false);
  const [userCredentials, setUserCredentials] = useState(null);
  const { id, user_name, key, user_type } = useParams();
  const [isBookingConfirmed, setBookingConfirmed] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [userSchedule, setUserSchedule] = useState([]);
  const currentDate = new Date().toISOString().split("T")[0];
  const startTime = new Date(currentDate);
  startTime.setHours(6, 0, 0, 0);
  const endTime = new Date(currentDate);
  endTime.setHours(23, 0, 0, 0);
  

  const fetchUserSchedule = async () => {
    const apiUrl = "/api/get_user_schedule";
  
    try {
      const formattedStartTime = format(startTime, "yyyy-MM-dd'T'HH:mm:ss");
      const formattedEndTime = format(endTime, "yyyy-MM-dd'T'HH:mm:ss");
  
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            user_id: parseInt(id),
            start_time: formattedStartTime,
            end_time: formattedEndTime,
        }),
    });
  

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error response text:", errorText);
      throw new Error(`Error: ${response.status} - ${response.statusText}`);
    }

    const userScheduleData = await response.json();
  
    if (Array.isArray(userScheduleData)) {
        setUserSchedule(userScheduleData);
    } else {
        console.error('Error: Received data is not an array.');
    }
} catch (error) {
    console.error('Error fetching user schedule:', error);
}
};
  
  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleTimeSlotSelection = (selectedTime) => {
    // Check if the selected time slot is already in the list
    const isAlreadySelected = selectedTimeSlots.some(
        (timeSlot) =>
            timeSlot.start_time === selectedTime.start_time &&
            timeSlot.end_time === selectedTime.end_time
    );

    if (isAlreadySelected) {
        // If already selected, remove it from the list
        setSelectedTimeSlots((prevSelectedTimeSlots) =>
            prevSelectedTimeSlots.filter(
                (timeSlot) =>
                    timeSlot.start_time !== selectedTime.start_time ||
                    timeSlot.end_time !== selectedTime.end_time
            )
        );
    } else {
        // If not selected, add it to the list with student_id
        setSelectedTimeSlots((prevSelectedTimeSlots) => [
            ...prevSelectedTimeSlots,
            { ...selectedTime, student_id: userCredentials.id },
        ]);
    }
};

  useEffect(() => {
    fetchUserCredentials(id, user_name, key, user_type);
    fetchTeachers();
    fetchUserSchedule();
  }, [id, user_name, key, user_type]);

  useEffect(() => {
    // Fetch teacher availability when selectedTeacher or selectedDate changes
    if (selectedTeacher && selectedDate) {
      fetchTeacherAvailability();
    }
  }, [selectedTeacher, selectedDate]);

  const fetchTeacherAvailability = async () => {
    const apiUrl = "/api/get_teacher_availability"; // Update with your server URL

    try {
      const responseData = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          teacher_id: parseInt(selectedTeacher), // Ensure id is a valid integer
          selected_date: selectedDate,
        }),
      });

      if (!responseData.ok) {
        throw new Error(
          `Error: ${responseData.status} - ${responseData.statusText}`
        );
      }

      const result = await responseData.json();

      // Log availability to the console
      console.log("Availability received from backend:", result.availability);

      setAvailableTimeSlots(result.availability);
    } catch (error) {
      console.error("Error fetching teacher availability:", error.message);
    }
  };

  const fetchUserCredentials = async (id, user_name, key, user_type) => {
    const apiUrl = "/api/get_user_credentials";

    try {
      const responseData = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: id,
          user_name: user_name,
          key: key,
          user_type: user_type,
        }),
      });

      if (!responseData.ok) {
        throw new Error(`Error: ${responseData.status} - ${responseData.statusText}`);
      }

      const result = await responseData.json();
      setUserCredentials(result);

      const checkUserUrl = "/api/check_user";
      const response = await fetch(checkUserUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: result.id,
          user_name: result.user_name,
          key: result.key,
          user_type: result.user_type,
        }),
      });

      if (response.ok) {
        const userExistsResult = await response.json();
        setUserExists(userExistsResult.exists);
      } else {
        console.error(`Error: ${response.status} - ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error fetching user credentials:", error.message);
    }
    
  };

  const fetchTeachers = async () => {
    try {
      const response = await fetch('/api/teachers');
      const data = await response.json();

      setTeachers(
        Array.isArray(data)
          ? data.map((teacher) => ({
              id: teacher.id,
              user_name: teacher.user_name,
            }))
          : []
      );
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  const resetForm = () => {
    setSelectedDate("");
    setSelectedTeacher("");
    setBookingConfirmed(false);
  };


  const handleBookNow = async () => {
    if (
      !selectedDate ||
      !startTime ||
      !endTime ||
      !selectedTeacher ||
      !isBookingConfirmed ||
      selectedTimeSlots.length === 0
    ) {
      alert("Please fill out all the required fields and confirm the booking before proceeding.");
      return;
    }
  
    const formattedStartTime = format(new Date(`${selectedDate} ${selectedTimeSlots[0].start_time}`), "yyyy-MM-dd HH:mm");
    const formattedEndTime = format(new Date(`${selectedDate} ${selectedTimeSlots[0].end_time}`), "yyyy-MM-dd HH:mm");
  
    const bookingData = {
      selectedDate: selectedDate,
      startTime: formattedStartTime,
      endTime: formattedEndTime,
      selectedTeacher: parseInt(selectedTeacher),
      selectedStudent: parseInt(id),
      selectedTimeSlots: selectedTimeSlots, // Include formatted time slots
  };
  
    // Check if the selected time slot is already booked
    const isAlreadyBooked = userSchedule.some((booking) => {
      const bookingStartTime = new Date(booking.startTime);
      const bookingEndTime = new Date(booking.endTime);
  
      return (
        formattedStartTime >= bookingStartTime &&
        formattedEndTime <= bookingEndTime
      );
    });
  
    if (isAlreadyBooked) {
      alert("This time slot is already booked. Please choose another time.");
      return;
    }
  
    const apiUrl = "/api/bookstudent_handler";
  
    try {
      console.log("Booking Data:", bookingData);
  
      const responseData = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bookingData),
      });
  
      if (!responseData.ok) {
        throw new Error(`Error: ${responseData.status} - ${responseData.statusText}`);
      }
  
      const result = await responseData.json();
      console.log("Booking successful:", result);
  
      resetForm();
    } catch (error) {
      console.error("Error during request setup:", error);
  
      if (error.response) {
        console.error("Server responded with errors:", error.response.data);
      }
    }
  };
  
 
  const teacherItems = Array.isArray(teachers)
    ? teachers.map((teacher) => (
        <MenuItem key={teacher.id} value={teacher.id}>
          {teacher.user_name}
        </MenuItem>
      ))
    : null;

    const generateTimeSlots = (start, end, intervalMinutes = 30) => {
      const timeSlots = [];
      let currentTime = new Date(start);
    
      while (currentTime <= end) {
        timeSlots.push({
          start_time: currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          end_time: new Date(currentTime.getTime() + intervalMinutes * 60000)
            .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        });
        currentTime = new Date(currentTime.getTime() + intervalMinutes * 60000);
      }
    
      return timeSlots;
    };
  
    const generatedTimeSlots = generateTimeSlots(startTime, endTime);

    const isTimeSlotAvailable = (startTime, endTime) => {
      const selectedStartTime = new Date(`${selectedDate} ${startTime}`);
      const selectedEndTime = new Date(`${selectedDate} ${endTime}`);
    
      // Check if the selected time slot is within the available time range
      const isWithinAvailableRange = availableTimeSlots.some((availability) => {
        const availabilityStartTime = new Date(availability.start_time);
        const availabilityEndTime = new Date(availability.end_time);
    
        return (
          selectedStartTime >= availabilityStartTime &&
          selectedEndTime <= availabilityEndTime &&
          availability.available === true
        );
      });
    
      // Check if the selected time slot is within the unavailable time range
      const isWithinUnavailableRange = availableTimeSlots.some((availability) => {
        const availabilityStartTime = new Date(availability.start_time);
        const availabilityEndTime = new Date(availability.end_time);
    
        return (
          selectedStartTime >= availabilityStartTime &&
          selectedEndTime <= availabilityEndTime &&
          availability.available === false
        );
      });
    
      // Check if the selected time slot is booked by other students
      const isBooked = userSchedule.some((booking) => {
        const bookingStartTime = new Date(booking.startTime);
        const bookingEndTime = new Date(booking.endTime);
    
        // Check for overlap with existing bookings
        const isOverlapWithBooking =
          selectedStartTime < bookingEndTime && selectedEndTime > bookingStartTime;
    
        return isOverlapWithBooking;
      });
    
      // Disable the button if it's within the unavailable range or out of the available range
      return !(isWithinUnavailableRange || (!isWithinAvailableRange && !isBooked));
    };

   if (!userExists) {
    return <div><ErrorPage /></div>;
  }
  return (
    <div className="App">
    <div className="backgroundImage"></div>
    <Container maxWidth="sm" sx={{ pt: 2 }}>
      <ChatHeader />
      <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
  <Button
    variant="contained"
    color="primary"
    onClick={handleOpenModal}
  >
    View schedule
  </Button>
</div>
      <FormControl fullWidth sx={{ marginBottom: 6, justifyContent: 'center' }}>
        <InputLabel id="course-select-label">
          <Typography variant="body1" component="span" style={{ color: 'black' }}>
            Student Name:
          </Typography>
          <Typography variant="body1" component="span" style={{ color: 'black', fontSize: '18px', fontWeight: 'bold' }}>
            {`${user_name}`}
          </Typography>
          
        </InputLabel>
      </FormControl>
      <Dialog open={isModalOpen} onClose={handleCloseModal}>
        <DialogTitle>Schedule</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            Schedule for {user_name}
          </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                
                  <TableCell>Teacher</TableCell>
                  <TableCell>Start Time</TableCell>
                  <TableCell>End Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userSchedule.map((data) => (
                  <TableRow key={data.id}>
                    
                    <TableCell>{data.teacher}</TableCell>
                    <TableCell>{data.startTime}</TableCell>
                    <TableCell>{data.endTime}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
        <Grid item xs={12}>
          <FormControl fullWidth sx={{ marginBottom: 2, justifyContent: 'center' }}>
            <TextField
              select
              label="Select Teacher"
              id="teacher-select"
              value={selectedTeacher}
              onChange={(e) => setSelectedTeacher(e.target.value)}
              SelectProps={{
                renderValue: (value) => {
                  const selectedTeacherObj = teachers.find((teacher) => teacher.id === value);
                  return selectedTeacherObj ? selectedTeacherObj.user_name : '';
                },
              }}
            >
              {teacherItems}
            </TextField>
          </FormControl>
        </Grid>

        {selectedTeacher && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Select Date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: currentDate }}
              />
            </Grid>

            {selectedDate &&
  generatedTimeSlots.map((timeSlot) => (
    <Grid key={timeSlot.start_time} item xs={3}>
      <Button
        variant="contained"
        disabled={
          timeSlot.start_time < userCredentials.start_time ||
          timeSlot.end_time > userCredentials.end_time ||
          !isTimeSlotAvailable(timeSlot.start_time, timeSlot.end_time)
        }
        onClick={() => handleTimeSlotSelection(timeSlot)}
        style={{
          backgroundColor: selectedTimeSlots.some(
            (selected) =>
              selected.start_time === timeSlot.start_time &&
              selected.end_time === timeSlot.end_time
          )
            ? "red"
            : "blue",
          ...(timeSlot.start_time < userCredentials.start_time ||
          timeSlot.end_time > userCredentials.end_time ||
          !isTimeSlotAvailable(timeSlot.start_time, timeSlot.end_time)
            ? { backgroundColor: "gray" }
            : {}),
        }}
      >
        <Typography variant="body2" style={{ color: 'white' }}>
          {`${timeSlot.start_time}`}
        </Typography>
        <Typography variant="body2" style={{ color: 'white', display: 'none' }}>
          {`${timeSlot.end_time}`}
        </Typography>
      </Button>
    </Grid>
  ))}
{selectedTimeSlots.length > 0 && endTime && (
  <React.Fragment>
    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={isBookingConfirmed}
            onChange={(e) => setBookingConfirmed(e.target.checked)}
            color="primary"
          />
        }
        label="I confirm the Booking"
      />
    </Grid>
  </React.Fragment>
)}
            {isBookingConfirmed && (
              <Grid item xs={12}>
                <Button variant="contained" color="primary" onClick={handleBookNow}>
                  Book Now
                </Button>
              </Grid>
            )}
          </Grid>
        )}
      </Container>
    </div>
  );
}

export default App;
